import { TransactionLineItem } from 'webReservations/checkout/apiHelpers';
import { Reservation } from 'webReservations/reservations/apiHelpers';
import { api } from '@shared/api';
import type {
  EventTierTicketSelection,
  SelectedAddOn,
  TierTableInventory,
} from '@shared/types/restaurantEvents';
import type { RestaurantEvent } from '@shared/types/restaurants';

export const getRestaurantEventById = async (
  restaurantId: string,
  restaurantEventId: string,
): Promise<RestaurantEvent> =>
  api.get(
    `/restaurants/${restaurantId}/restaurant-events/${restaurantEventId}`,
  );

export const getRestaurantEventByUrlSlug = async (
  restaurantId: string,
  urlSlug: string,
): Promise<RestaurantEvent> =>
  api.get(
    `/restaurants/${restaurantId}/restaurant-events/by-slug/${encodeURIComponent(urlSlug)}`,
  );

export const getLegacyRestaurantEventByUrlSlug = async (
  restaurantId: string,
  urlSlug: string,
  urlDate: string,
): Promise<RestaurantEvent> =>
  api.get(
    `/restaurants/${restaurantId}/restaurant-events/by-slug/${encodeURIComponent(urlSlug)}/${encodeURIComponent(urlDate)}`,
  );

export const getRestaurantEventsByDate = async (
  restaurantId: string,
  date: string,
): Promise<RestaurantEvent[]> =>
  api.get(`/restaurants/${restaurantId}/restaurant-events?date=${date}`);

export type {
  EventTierTicketSelection,
  InventoryTable,
  SelectedAddOn,
  TierTableInventory,
} from '@shared/types/restaurantEvents';

/** The legacy event page sells one tier per purchase, with at most one table. */
interface SingleTierTicketSelection {
  restaurantEventTierId: string;
  guestCount: number;
  selectedFloorPlanTableId?: string | null;
}

/** The redesigned event page sells tickets across several tiers in one purchase. */
interface MultiTierTicketSelection {
  tiers: EventTierTicketSelection[];
}

type EventTicketSelection =
  | SingleTierTicketSelection
  | MultiTierTicketSelection;

type EventBookingProps = {
  restaurantId: string;
  eventId: string;
  selectedAddOns?: SelectedAddOn[];
} & EventTicketSelection;

const toTierPayload = ({
  restaurantEventTierId,
  quantity,
  floorPlanTableIds,
}: EventTierTicketSelection) => ({
  restaurantEventTierId,
  guestCount: quantity,
  ...(floorPlanTableIds
    ? { selectedFloorPlanTableIds: floorPlanTableIds }
    : {}),
});

const toSingleTierPayload = ({
  restaurantEventTierId,
  guestCount,
  selectedFloorPlanTableId,
}: SingleTierTicketSelection) => ({
  restaurantEventTierId,
  guestCount,
  selectedFloorPlanTableId: selectedFloorPlanTableId ?? null,
});

// Both flows go to the same endpoints as a list of tiers: the server takes the
// total from the tiers, so no top-level guest count is sent.
const getTiersPayload = (ticketSelection: EventTicketSelection) => ({
  tiers:
    'tiers' in ticketSelection
      ? ticketSelection.tiers.map(toTierPayload)
      : [toSingleTierPayload(ticketSelection)],
});

export const bookEventReservation = async ({
  restaurantId,
  eventId,
  transactionLineItems,
  tax,
  fee,
  total,
  selectedAddOns,
  marketingOptIn,
  email,
  ...ticketSelection
}: EventBookingProps & {
  transactionLineItems: TransactionLineItem[];
  tax: number;
  fee: number;
  total: number;
  marketingOptIn?: boolean;
  /** For the event's own messages: kept on the reservation, never on the account. */
  email?: string;
}): Promise<{
  reservation: Reservation;
  paymentIntentClientSecret: string | null;
}> => {
  return api.post(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}/reservations`,
    {
      ...getTiersPayload(ticketSelection),
      transactionLineItems,
      tax,
      fee,
      total,
      selectedAddOns,
      marketingOptIn,
      email,
    },
  );
};

export const getRestaurantEventPricing = async ({
  restaurantId,
  eventId,
  selectedAddOns,
  ...ticketSelection
}: EventBookingProps): Promise<{
  transactionLineItems: TransactionLineItem[];
  tax: number;
  fee: number;
  total: number;
  subtotal: number;
}> => {
  return api.post(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}/reservations/pricing`,
    {
      ...getTiersPayload(ticketSelection),
      selectedAddOns,
    },
  );
};

export interface TierTablePricing {
  floorPlanTableId: string;
  listingId: string;
  price: number;
  publicName: string | null;
}

export const getAvailableTablesForTier = async (
  restaurantId: string,
  restaurantEventId: string,
  restaurantEventTierId: string,
  guestCount: number,
): Promise<TierTablePricing[]> => {
  return api.get(
    `/restaurants/${restaurantId}/restaurant-events/${restaurantEventId}/tiers/${restaurantEventTierId}/available-tables?guestCount=${guestCount}`,
  );
};

export const getTableInventoryForTier = async (
  restaurantId: string,
  restaurantEventId: string,
  restaurantEventTierId: string,
): Promise<TierTableInventory> => {
  return api.get(
    `/restaurants/${restaurantId}/restaurant-events/${restaurantEventId}/tiers/${restaurantEventTierId}/table-inventory`,
  );
};
